import { Preferences } from '@capacitor/preferences';

export interface AsyncKeyValueStorage {
  getItem(key: string): Promise<string | null>;
  setItem(key: string, value: string): Promise<void>;
  removeItem(key: string): Promise<void>;
}

// The plugin's web implementation prefixes every key with the group name, except for
// the one literal value 'NativeStorage', which makes the prefix empty and keeps these
// keys byte-identical to the ones deployed web sessions already sit under.
const PREFERENCES_GROUP = 'NativeStorage';

let configured: Promise<void> | null = null;

// `configure` is an ordinary async plugin call the runtime does not sequence against the
// reads that follow it: a `get` that wins the race reads a prefixed key that does not
// exist, and the caller sees an empty store.
function ensureConfigured(): Promise<void> {
  // Memoising a *rejected* configure would leave every later read and write awaiting it, so a
  // single transient failure would disable storage for the rest of the WebView's life. Dropping
  // the cache on rejection lets the next call retry; rethrowing keeps that call loud.
  configured ??= Preferences.configure({ group: PREFERENCES_GROUP }).catch((error: unknown) => {
    configured = null;
    throw error;
  });

  return configured;
}

// The plugin's web implementation dereferences a bare `window`, which is a
// ReferenceError rather than `undefined` under a prerender.
const isBrowser = (): boolean => typeof window !== 'undefined';

/**
 * `Preferences.clear()` is deliberately absent: under the `NativeStorage` group the empty
 * prefix makes it wipe the whole backing store — all of `localStorage` on web, the whole
 * `UserDefaults` suite on iOS — rather than only the keys written through here.
 */
export const preferencesStorage: AsyncKeyValueStorage = {
  async getItem(key: string): Promise<string | null> {
    if (!isBrowser()) {
      return null;
    }

    await ensureConfigured();
    const { value } = await Preferences.get({ key });

    return value;
  },

  async setItem(key: string, value: string): Promise<void> {
    if (!isBrowser()) {
      return;
    }

    await ensureConfigured();
    await Preferences.set({ key, value });
  },

  async removeItem(key: string): Promise<void> {
    if (!isBrowser()) {
      return;
    }

    await ensureConfigured();
    await Preferences.remove({ key });
  },
};

/**
 * Returns the stored keys that start with `prefix`, prefix included.
 *
 * The prefix is mandatory rather than a convenience: under the `NativeStorage` group the
 * plugin's own key prefix is empty, so `Preferences.keys()` enumerates the entire backing
 * store — the whole of `UserDefaults.standard` on iOS, Apple's own keys included.
 */
export async function listPreferenceKeys(prefix: string): Promise<string[]> {
  if (!prefix) {
    throw new Error('listPreferenceKeys needs a non-empty prefix');
  }

  if (!isBrowser()) {
    return [];
  }

  await ensureConfigured();
  const { keys } = await Preferences.keys();

  return keys.filter((key) => key.startsWith(prefix));
}
